import axios from "axios";
import { useEffect, useState, useMemo } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_URL } from "../config";

interface Ticket {
  id: number;
  ticket_number: string;
  email: string;
  title: string;
  name: string;
  description?: string;
  status: "new" | "open" | "in_progress" | "resolved" | "closed";
  created_at: string;
}

const STATUS_STYLES: Record<string, { bg: string; text: string; border: string; dot: string }> = {
  new: { bg: "#eff6ff", text: "#1e40af", border: "#bfdbfe", dot: "#3b82f6" },
  open: { bg: "#ecfeff", text: "#0e7490", border: "#a5f3fc", dot: "#06b6d4" },
  in_progress: { bg: "#fffbeb", text: "#b45309", border: "#fde68a", dot: "#f59e0b" },
  resolved: { bg: "#f0fdf4", text: "#15803d", border: "#bbf7d0", dot: "#16a34a" },
  closed: { bg: "#f1f5f9", text: "#475569", border: "#e2e8f0", dot: "#64748b" },
};

const ListTicket = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  // Modals
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);

  const [selectedTicket, setSelectedTicket] = useState<Ticket | null>(null);

  // Form states
  const [newStatus, setNewStatus] = useState<string>("new");
  const [emailSubject, setEmailSubject] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Récupération du token
  const token = localStorage.getItem("token_admin_silverjarz");

  const config_token = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const fetchTickets = async (page = 1) => {
    try {
      setIsLoading(true);
      const response = await axios.get(`${API_URL}/tickets?page=${page}`, config_token);
      setTickets(response.data.data);
      setCurrentPage(response.data.pagination.current_page);
      setLastPage(response.data.pagination.last_page);
    } catch (error) {
      console.error("Error fetching tickets:", error);
      toast.error("Error fetching tickets");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const filteredTickets = useMemo(() => {
    if (!searchTerm) return tickets;
    const term = searchTerm.toLowerCase();
    return tickets.filter(
      (t) =>
        t.ticket_number.toLowerCase().includes(term) ||
        t.name.toLowerCase().includes(term) ||
        t.email.toLowerCase().includes(term) ||
        t.title.toLowerCase().includes(term)
    );
  }, [tickets, searchTerm]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "--";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatStatus = (status: string) => {
    if (!status) return "";
    return status.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  // Delete Action
  const handleDeleteClick = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTicket) return;
    try {
      await axios.delete(`${API_URL}/ticket/${selectedTicket.id}`, config_token);
      toast.success("Ticket deleted successfully");
      setTickets((prev) => prev.filter((t) => t.id !== selectedTicket.id));
    } catch (error: any) {
      toast.error("Error deleting ticket");
    } finally {
      setShowDeleteModal(false);
      setSelectedTicket(null);
    }
  };

  // Edit / Details Action
  const handleEditClick = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setNewStatus(ticket.status);
    setShowEditModal(true);
  };

  const handleUpdateStatus = async () => {
    if (!selectedTicket) return;
    try {
      setIsSubmitting(true);
      const res = await axios.put(
        `${API_URL}/ticket/${selectedTicket.id}`,
        { status: newStatus },
        config_token
      );

      if (res.data.success) {
        toast.success("Ticket status updated successfully");
        setTickets((prev) =>
          prev.map((t) => (t.id === selectedTicket.id ? { ...t, status: newStatus as any } : t))
        );
        setShowEditModal(false);
      }
    } catch (error) {
      toast.error("Error updating ticket status");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Send Email Action
  const handleEmailClick = (ticket: Ticket) => {
    setSelectedTicket(ticket);
    setEmailSubject(`Response to Ticket #${ticket.ticket_number}`);
    setEmailMessage("");
    setShowEmailModal(true);
  };

  const handleSendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTicket) return;

    try {
      setIsSubmitting(true);
      await axios.post(
        `${API_URL}/ticket/${selectedTicket.id}/send-email`,
        {
          subject: emailSubject,
          message: emailMessage,
        },
        config_token
      );
      toast.success("Email sent successfully");
      setShowEmailModal(false);
    } catch (error) {
      toast.error("Error sending email");
    } finally {
      setIsSubmitting(false);
    }
  };

  const clearSearch = () => setSearchTerm("");

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= lastPage) fetchTickets(page);
  };

  const getPaginationRange = (current: number, total: number) => {
    const delta = 1;
    const range: (number | string)[] = [];
    for (let i = 1; i <= total; i++) {
      if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
        range.push(i);
      } else if (range[range.length - 1] !== "...") {
        range.push("...");
      }
    }
    return range;
  };

  function SkeletonLoader() {
    return (
      <div style={{ width: "100%", minHeight: "100vh", backgroundColor: "#f4f6f8", padding: "20px 20px", boxSizing: "border-box" }}>
        <style>{`
          @keyframes shimmer {
            0% { background-position: -200% 0; }
            100% { background-position: 200% 0; }
          }
        `}</style>

        {/* Header Skeleton */}
        <div className="d-flex justify-content-between align-items-end mb-4">
          <div>
            <div className="d-flex align-items-center gap-2 mb-1">
              <div style={{
                width: 180,
                height: 28,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 6,
                animation: "shimmer 1.5s infinite"
              }}></div>
              <div style={{
                width: 60,
                height: 20,
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                backgroundSize: "200% 100%",
                borderRadius: 12,
                animation: "shimmer 1.5s infinite"
              }}></div>
            </div>
            <div style={{
              width: 280,
              height: 18,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 4,
              animation: "shimmer 1.5s infinite"
            }}></div>
          </div>
        </div>

        {/* Main Card Skeleton */}
        <div style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
          marginTop: "15px",
        }}>
          {/* Filter Bar Skeleton */}
          <div style={{ padding: "16px 20px", borderBottom: "1px solid #edf2f7" }}>
            <div className="row g-3 align-items-center">
              <div className="col-12 col-md-6 col-lg-5">
                <div style={{
                  width: "100%",
                  height: 42,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 8,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              </div>
            </div>
          </div>

          {/* Table Header Skeleton */}
          <div style={{ padding: "12px 16px", backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
            <div className="row">
              {["Number", "Title", "Contact", "Email", "Date", "Status", "Actions"].map((_, i) => (
                <div key={i} className="col" style={{ padding: "0 8px" }}>
                  <div style={{
                    width: i === 0 ? 60 : i === 1 ? 50 : i === 2 ? 60 : i === 3 ? 50 : i === 4 ? 40 : i === 5 ? 50 : 60,
                    height: 14,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                    backgroundSize: "200% 100%",
                    borderRadius: 4,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                </div>
              ))}
            </div>
          </div>

          {/* Table Rows Skeleton */}
          {[...Array(8)].map((_, rowIndex) => (
            <div
              key={rowIndex}
              style={{
                padding: "14px 16px",
                borderBottom: rowIndex < 7 ? "1px solid #f1f5f9" : "none",
                display: "flex",
                alignItems: "center"
              }}
            >
              {[...Array(7)].map((_, colIndex) => (
                <div key={colIndex} style={{ flex: 1, padding: "0 8px" }}>
                  <div style={{
                    width: colIndex === 0 ? 80 : colIndex === 1 ? 120 : colIndex === 2 ? 100 : colIndex === 3 ? 140 : colIndex === 4 ? 80 : colIndex === 5 ? 80 : 80,
                    height: 14,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                    backgroundSize: "200% 100%",
                    borderRadius: 4,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                </div>
              ))}
            </div>
          ))}

          {/* Pagination Skeleton */}
          <div style={{
            padding: "12px 20px",
            borderTop: "1px solid #edf2f7",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center"
          }}>
            <div style={{
              width: 120,
              height: 16,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
              backgroundSize: "200% 100%",
              borderRadius: 4,
              animation: "shimmer 1.5s infinite"
            }}></div>
            <div style={{ display: "flex", gap: "6px" }}>
              {[...Array(5)].map((_, i) => (
                <div key={i} style={{
                  width: i === 0 || i === 4 ? 70 : 32,
                  height: 32,
                  background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)",
                  backgroundSize: "200% 100%",
                  borderRadius: 6,
                  animation: "shimmer 1.5s infinite"
                }}></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        backgroundColor: "#f4f6f8",
        padding: "20px 20px",
        boxSizing: "border-box",
        color: "#1e293b",
      }}
    >
      <ToastContainer position="top-right" autoClose={3000} />

      {/* SECTION EN-TÊTE */}
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div>
          <div className="d-flex align-items-center gap-2 mb-1">
            <h1 style={{ color: "#294b5a", fontSize: "22px", fontWeight: 700, margin: 0 }}>
              Manage Tickets
            </h1>
            <span
              style={{
                backgroundColor: "#294b5a",
                color: "#d3be76",
                fontSize: "11px",
                fontWeight: 700,
                padding: "2px 8px",
                borderRadius: "12px",
              }}
            >
              System
            </span>
          </div>
          <p style={{ color: "#64748b", fontSize: "13.5px", margin: 0 }}>
            Overview of <strong style={{ color: "#294b5a" }}>{filteredTickets.length}</strong> of{" "}
            <strong>{tickets.length}</strong> tickets on this page.
          </p>
        </div>
      </div>

      {/* BLOC PRINCIPAL */}
      <div
        style={{
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          border: "1px solid #e2e8f0",
          boxShadow: "0 4px 16px -2px rgba(0,0,0,0.03)",
          overflow: "hidden",
          marginTop: "15px",
        }}
      >
        {/* BARRE DE FILTRES */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid #edf2f7", backgroundColor: "#ffffff" }}>
          <div className="row g-3 align-items-center">
            <div className="col-12 col-md-6 col-lg-5">
              <div className="position-relative">
                <input
                  type="text"
                  placeholder="Search by number, name, email, title..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "9px 36px 9px 38px",
                    border: "1px solid #cbd5e1",
                    borderRadius: "8px",
                    fontSize: "13px",
                    outline: "none",
                    color: "#0f172a",
                    backgroundColor: "#f8fafc",
                  }}
                />
                <i
                  className="fa-solid fa-magnifying-glass"
                  style={{
                    position: "absolute",
                    left: "14px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: "13px",
                    color: "#94a3b8",
                  }}
                ></i>
                {searchTerm && (
                  <button
                    type="button"
                    onClick={clearSearch}
                    style={{
                      position: "absolute",
                      right: "12px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      background: "none",
                      border: "none",
                      cursor: "pointer",
                      color: "#94a3b8",
                    }}
                  >
                    <i className="fa-solid fa-xmark" style={{ fontSize: "14px" }}></i>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* TABLEAU */}
        {isLoading ? (
          <SkeletonLoader />
        ) : (
          <>
            <div className="table-responsive" style={{ margin: 0 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left" }}>
                <thead>
                  <tr style={{ backgroundColor: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700 }}>Number</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700 }}>Title</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700 }}>Contact</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700 }}>Email</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700 }}>Date</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, textAlign: "center" }}>Status</th>
                    <th style={{ padding: "12px 16px", color: "#294b5a", fontSize: "12px", fontWeight: 700, textAlign: "right" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTickets.length > 0 ? (
                    filteredTickets.map((item) => {
                      const statusStyle = STATUS_STYLES[item.status] || STATUS_STYLES.closed;
                      return (
                        <tr key={item.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ padding: "14px 16px", fontSize: "13.5px", fontWeight: 600, color: "#0f172a" }}>
                            {item.ticket_number}
                          </td>
                          <td style={{ padding: "14px 16px", fontSize: "13.5px", color: "#1f2937" }}>
                            {item.title}
                          </td>
                          <td style={{ padding: "14px 16px", fontSize: "13.5px", color: "#475569" }}>
                            {item.name}
                          </td>
                          <td style={{ padding: "14px 16px", fontSize: "13.5px", color: "#475569" }}>
                            {item.email}
                          </td>
                          <td style={{ padding: "14px 16px", fontSize: "13.5px", color: "#475569" }}>
                            {formatDate(item.created_at)}
                          </td>
                          <td style={{ padding: "14px 16px", textAlign: "center" }}>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "3px 10px",
                                borderRadius: "20px",
                                fontSize: "12px",
                                fontWeight: 600,
                                backgroundColor: statusStyle.bg,
                                color: statusStyle.text,
                                border: `1px solid ${statusStyle.border}`,
                              }}
                            >
                              <span style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: statusStyle.dot }}></span>
                              {formatStatus(item.status)}
                            </span>
                          </td>
                          <td style={{ padding: "14px 16px", textAlign: "right" }}>
                            <div style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                              {/* Bouton Details / Edit */}
                              <button
                                type="button"
                                onClick={() => handleEditClick(item)}
                                style={{
                                  border: "1px solid #e2e8f0",
                                  backgroundColor: "#ffffff",
                                  color: "#294b5a",
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "6px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                }}
                                title="View Details / Update Status"
                              >
                                <i className="fa-solid fa-pen-to-square" style={{ fontSize: "13px" }}></i>
                              </button>

                              {/* Bouton Envoi Email */}
                              <button
                                type="button"
                                onClick={() => handleEmailClick(item)}
                                style={{
                                  border: "1px solid #e2e8f0",
                                  backgroundColor: "#ffffff",
                                  color: "#0284c7",
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "6px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                }}
                                title="Send Email"
                              >
                                <i className="fa-solid fa-paper-plane" style={{ fontSize: "13px" }}></i>
                              </button>

                              {/* Bouton Suppression */}
                              <button
                                type="button"
                                onClick={() => handleDeleteClick(item)}
                                style={{
                                  border: "1px solid #e2e8f0",
                                  backgroundColor: "#ffffff",
                                  color: "#64748b",
                                  width: "32px",
                                  height: "32px",
                                  borderRadius: "6px",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  cursor: "pointer",
                                }}
                                title="Delete"
                              >
                                <i className="fa-solid fa-trash-can" style={{ fontSize: "13px" }}></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={7} style={{ textAlign: "center", padding: "48px 20px", color: "#64748b", fontSize: "13.5px" }}>
                        No tickets found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            {tickets.length > 0 && !searchTerm && lastPage > 1 && (
              <div style={{ padding: "12px 20px", borderTop: "1px solid #edf2f7", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: "13px", color: "#64748b" }}>
                  Page <strong>{currentPage}</strong> of <strong>{lastPage}</strong>
                </div>
                <div style={{ display: "flex", gap: "6px" }}>
                  <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", cursor: currentPage === 1 ? "not-allowed" : "pointer" }}>
                    Previous
                  </button>
                  {getPaginationRange(currentPage, lastPage).map((item, index) => (
                    <button
                      key={index}
                      onClick={() => typeof item === "number" && handlePageChange(item)}
                      style={{
                        width: "32px",
                        height: "32px",
                        borderRadius: "6px",
                        border: currentPage === item ? "none" : "1px solid #cbd5e1",
                        backgroundColor: currentPage === item ? "#294b5a" : "#ffffff",
                        color: currentPage === item ? "#ffffff" : "#334155",
                      }}
                    >
                      {item}
                    </button>
                  ))}
                  <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === lastPage} style={{ padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", cursor: currentPage === lastPage ? "not-allowed" : "pointer" }}>
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* MODAL EDIT / DETAILS */}
      {showEditModal && selectedTicket && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(15, 23, 42, 0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050, padding: "16px" }}>
          <div style={{ backgroundColor: "#ffffff", borderRadius: "16px", width: "100%", maxWidth: "550px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
            <div style={{ padding: "20px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ fontSize: "16px", fontWeight: 700, color: "#294b5a", margin: 0 }}>
                Ticket Details — {selectedTicket.ticket_number}
              </h3>
              <button onClick={() => setShowEditModal(false)} style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b" }}>&times;</button>
            </div>
            <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "16px" }}>
              <div>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>Contact</label>
                <div style={{ fontSize: "14px", color: "#0f172a", fontWeight: 600 }}>{selectedTicket.name} ({selectedTicket.email})</div>
              </div>
              <div>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>Title</label>
                <div style={{ fontSize: "14px", color: "#0f172a" }}>{selectedTicket.title}</div>
              </div>
              {selectedTicket.description && (
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b" }}>Description</label>
                  <div style={{ fontSize: "13.5px", color: "#334155", backgroundColor: "#f8fafc", padding: "12px", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
                    {selectedTicket.description}
                  </div>
                </div>
              )}
              <div>
                <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "6px", display: "block" }}>Update Status</label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "14px", outline: "none" }}
                >
                  <option value="new">New</option>
                  <option value="open">Open</option>
                  <option value="resolved">Resolved</option>
                  <option value="closed">Closed</option>
                </select>
              </div>
            </div>
            <div style={{ padding: "16px 24px", backgroundColor: "#f8fafc", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: "12px" }}>
              <button onClick={() => setShowEditModal(false)} style={{ padding: "9px 16px", borderRadius: "8px", border: "1px solid #cbd5e1", backgroundColor: "#ffffff", color: "#64748b", cursor: "pointer" }}>Cancel</button>
              <button onClick={handleUpdateStatus} disabled={isSubmitting} style={{ padding: "9px 16px", borderRadius: "8px", border: "none", backgroundColor: "#294b5a", color: "#ffffff", cursor: "pointer", fontWeight: 600 }}>
                {isSubmitting ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL EMAIL */}
      {showEmailModal && selectedTicket && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(15, 23, 42, 0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050, padding: "16px" }}>
          <div style={{ backgroundColor: "#ffffff", borderRadius: "16px", width: "100%", maxWidth: "550px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
            <div style={{ padding: "20px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h3 style={{ fontSize: "16px", fontWeight: 700, color: "#294b5a", margin: 0 }}>
                Send Email to {selectedTicket.name}
              </h3>
              <button onClick={() => setShowEmailModal(false)} style={{ background: "none", border: "none", fontSize: "18px", cursor: "pointer", color: "#64748b" }}>&times;</button>
            </div>
            <form onSubmit={handleSendEmail}>
              <div style={{ padding: "24px", display: "flex", flexDirection: "column", gap: "16px" }}>
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "4px", display: "block" }}>To</label>
                  <input type="email" value={selectedTicket.email} disabled style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #e2e8f0", backgroundColor: "#f8fafc", color: "#64748b" }} />
                </div>
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "4px", display: "block" }}>Subject</label>
                  <input type="text" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} required style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "14px" }} />
                </div>
                <div>
                  <label style={{ fontSize: "12px", fontWeight: 600, color: "#64748b", marginBottom: "4px", display: "block" }}>Message</label>
                  <textarea rows={5} value={emailMessage} onChange={(e) => setEmailMessage(e.target.value)} required placeholder="Write your message here..." style={{ width: "100%", padding: "10px", borderRadius: "8px", border: "1px solid #cbd5e1", fontSize: "14px", resize: "none" }}></textarea>
                </div>
              </div>
              <div style={{ padding: "16px 24px", backgroundColor: "#f8fafc", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: "12px" }}>
                <button type="button" onClick={() => setShowEmailModal(false)} style={{ padding: "9px 16px", borderRadius: "8px", border: "1px solid #cbd5e1", backgroundColor: "#ffffff", color: "#64748b", cursor: "pointer" }}>Cancel</button>
                <button type="submit" disabled={isSubmitting} style={{ padding: "9px 16px", borderRadius: "8px", border: "none", backgroundColor: "#0284c7", color: "#ffffff", cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", gap: "6px" }}>
                  <i className="fa-solid fa-paper-plane"></i>
                  {isSubmitting ? "Sending..." : "Send Email"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* MODAL DE SUPPRESSION */}
      {showDeleteModal && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, backgroundColor: "rgba(15, 23, 42, 0.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050, padding: "16px" }}>
          <div style={{ backgroundColor: "#ffffff", borderRadius: "16px", width: "100%", maxWidth: "440px", overflow: "hidden", border: "1px solid #e2e8f0" }}>
            <div style={{ padding: "28px 24px 20px 24px", textAlign: "center" }}>
              <div style={{ width: "56px", height: "56px", borderRadius: "50%", backgroundColor: "#fef2f2", border: "1px solid #fecaca", display: "inline-flex", alignItems: "center", justifyContent: "center", marginBottom: "16px" }}>
                <i className="fa-solid fa-trash-can" style={{ color: "#dc2626", fontSize: "22px" }}></i>
              </div>
              <h2 style={{ fontSize: "18px", fontWeight: 700, color: "#294b5a", margin: "0 0 8px 0" }}>Delete Ticket?</h2>
              <p style={{ fontSize: "13.5px", color: "#64748b", margin: 0 }}>Are you sure you want to delete this ticket? This action cannot be undone.</p>
              {selectedTicket && (
                <div style={{ marginTop: "16px", padding: "12px 16px", backgroundColor: "#f8fafc", borderRadius: "10px", border: "1px solid #e2e8f0", textAlign: "left" }}>
                  <div style={{ fontSize: "13.5px", fontWeight: 700, color: "#0f172a" }}>{selectedTicket.ticket_number} — {selectedTicket.title}</div>
                  <div style={{ fontSize: "12px", color: "#64748b" }}>From: {selectedTicket.name} ({selectedTicket.email})</div>
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: "12px", padding: "16px 24px 20px 24px", backgroundColor: "#ffffff" }}>
              <button type="button" onClick={() => setShowDeleteModal(false)} style={{ flex: 1, padding: "10px 16px", fontSize: "13.5px", fontWeight: "600", color: "#64748b", backgroundColor: "#ffffff", border: "1px solid #cbd5e1", borderRadius: "8px", cursor: "pointer" }}>Cancel</button>
              <button type="button" onClick={handleConfirmDelete} style={{ flex: 1, padding: "10px 16px", fontSize: "13.5px", fontWeight: "600", color: "#ffffff", backgroundColor: "#dc2626", border: "none", borderRadius: "8px", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                <i className="fa-solid fa-trash-can" style={{ fontSize: "13px" }}></i>
                Delete Ticket
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListTicket;